import { Button, Form, Input, Radio, Select, Upload } from "antd";
import { useState } from "react";
import { useMyContext } from "../../context/MyContext";
import Header from "../header/Header";
import DashboardLayout from "../layout";
import { api } from "../../config/api";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@tanstack/react-query";
import { getUserQuoteById } from "../../lib/quotes";
import { ticketPriorities, ticketTypes } from "./data";
import { useNavigate } from "react-router-dom";

const CreateTicket = () => {
  const { user, token } = useMyContext();
  const navigate = useNavigate();
  const [refType, setRefType] = useState("quote"); // 'quote' or 'order'

  const { data: quotes, isLoading } = useQuery(
    ["quotes-list"],
    async () => {
      const res = await getUserQuoteById(user?.uid);
      return res;
    },
    { refetchOnWindowFocus: false }
  );

  const addMutation = useMutation(
    ["tickets"],
    async (data) => {
      return await api.post("/tickets", data, {
        headers: { Authorization: `Bearer ${token}` }
      });
    },
    {
      onSuccess: (response) => {
        toast.success("Ticket created successfully!");
        if (response?.status === 200) {
          navigate(`/tickets/${response?.data?.data?._id}`);
        }
      },
      onError: (error) => {
        console.error("Error creating ticket:", error);
        toast.error("Ticket not created. Please try again.");
      },
    }
  );

  const handleFinish = async (values) => {
    if (!token) {
      toast.error("You need to be logged in to create a ticket.");
      return;
    }
    addMutation.mutate({ ...values, userId: user?.uid });
  };

  return (
    <DashboardLayout>
      <Header title="New ticket" />
      <main className="p-5 bg-cus-gray">
        <div className="bg-white p-5 rounded-[22px] flex flex-col space-y-2">
          <h3 className="font-medium text-2xl">Add the following details</h3>
          <Form onFinish={handleFinish} className="ticket-form">
            <span className="text-[#777777] text-lg">
              How can we assist you?
            </span>
            <Form.Item
              rules={[
                {
                  required: true,
                  message: "Please select related quote!",
                },
              ]}
              // label="Select quote number"
              name="type"
              className="mt-2"
            >
              <Radio.Group className="flex flex-col space-y-2 text-[#777777]">
                <Radio
                  value="Tracking Information"
                  className="text-[#777777] text-sm"
                >
                  Tracking Information
                </Radio>
                <Radio
                  value="Delivery Status"
                  className="text-[#777777] text-sm"
                >
                  Delivery Status
                </Radio>
                <Radio
                  value="Product Inquiry"
                  className="text-[#777777] text-sm"
                >
                  Product Inquiry
                </Radio>
                <Radio value="Other" className="text-[#777777] text-sm">
                  Other
                </Radio>
              </Radio.Group>
            </Form.Item>
            <span className="text-[#777777] text-lg">Choose reference</span>
            <div className="mt-2 mb-1">
              <Radio.Group
                value={refType}
                onChange={(e) => setRefType(e.target.value)}
                className="flex flex-row space-x-6 text-[#777777]"
              >
                <Radio value="quote" className="text-[#777777] text-sm">
                  Quote No
                </Radio>
                <Radio value="order" className="text-[#777777] text-sm">
                  Order No
                </Radio>
              </Radio.Group>
            </div>

            {refType === "quote" && (
              <>
                <span className="text-[#777777] text-lg mt-4 block">Select quote no</span>
                <Form.Item
                  name="quoteNumber"
                  rules={[{ required: true, message: "Please select quote no!" }]}
                >
                  <Select
                    placeholder="Select option"
                    options={[
                      ...((quotes || []).map((quote) => ({
                        label: quote?.quoteNumber,
                        value: quote?.quoteNumber,
                      }))),
                      { label: "Other", value: "Other" },
                    ]}
                    showSearch
                    optionFilterProp="label"
                    bordered={false}
                    size="large"
                  />
                </Form.Item>
              </>
            )}

            {refType === "order" && (
              <>
                <span className="text-[#777777] text-lg mt-4 block">Select order no</span>
                <Form.Item
                  name="orderNumber"
                  rules={[{ required: true, message: "Please select order no!" }]}
                >
                  <Select
                    placeholder="Select option"
                    options={[
                      ...((quotes || [])
                        .filter((q) => q?.orderNumber)
                        .map((q) => ({ label: q.orderNumber, value: q.orderNumber }))),
                      { label: "Other", value: "Other" },
                    ]}
                    showSearch
                    optionFilterProp="label"
                    bordered={false}
                    size="large"
                  />
                </Form.Item>
              </>
            )}
            <span className="text-[#777777] text-lg">
              Select ticket priority
            </span>

            <Form.Item
              rules={[
                {
                  required: true,
                  message: "Please select ticket priority!",
                },
              ]}
              name="priority"
            >
              <Select
                placeholder="Select option"
                options={ticketPriorities}
                bordered={false}
                size="large"
              />
            </Form.Item>
            <span className="text-[#777777] text-lg">
              Describe your issue or question
            </span>

            <Form.Item name="description">
              <Input.TextArea
                className="bg-[#D9D9D914] text-lg bg-opacity-10 border-none"
                rows={5}
                placeholder="Enter description"
              />
            </Form.Item>

            <Form.Item className="flex items-center justify-end">
              <Button
                loading={addMutation.isLoading}
                htmlType="submit"
                type="primary"
                size="large"
                className="px-10"
              >
                {addMutation.isLoading ? "Submitting..." : "Submit"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </main>
    </DashboardLayout>
  );
};

export default CreateTicket;
